import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/layout';
import { useScrollReveal } from '../hooks/useScrollReveal';
import axios from 'axios';
import { SEO } from '../components/SEO';
import { logger } from '../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = ['All', 'Transformation', 'Leadership', 'Operating Models', 'Delivery', 'Culture', 'Strategy'];

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-AU', { 
    day: 'numeric',
    month: 'long', 
    year: 'numeric' 
  });
};

const HeroSection = () => {
  return (
    <section 
      className="min-h-[35vh] flex items-center bg-[#faf9f7]"
      data-testid="insights-hero"
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16 md:py-20">
        <h1 
          className="font-playfair text-4xl sm:text-5xl lg:text-6xl font-bold italic text-[#1a1a1a] mb-6 animate-fade-in-up"
          data-testid="insights-hero-title"
        >
          Insights
        </h1>
        <p 
          className="font-dm-sans text-lg md:text-xl text-[#4a4744] leading-relaxed max-w-3xl animate-fade-in-up animate-delay-200"
          data-testid="insights-hero-subtitle"
        >
          Thought pieces, articles, and perspectives on how organisations create the conditions for lasting change.
        </p>
      </div>
    </section>
  );
};

const CategoryFilter = ({ activeCategory, onCategoryChange }) => {
  return (
    <div className="flex flex-wrap gap-3 mb-12" data-testid="category-filter">
      {categories.map((category) => (
        <button
          key={category}
          onClick={() => onCategoryChange(category)}
          data-testid={`category-${category.toLowerCase()}`}
          className={`px-5 py-2 font-dm-sans text-sm font-medium transition-all duration-200 ${
            activeCategory === category
              ? 'bg-[#d4782c] text-white'
              : 'bg-white border border-[#e5e2dc] text-[#4a4744] hover:border-[#d4782c] hover:text-[#d4782c]'
          }`}
        >
          {category}
        </button>
      ))}
    </div>
  );
};

const ArticleCard = ({ article, index }) => {
  const [ref, isVisible] = useScrollReveal(0.1);

  return (
    <article 
      ref={ref}
      className={`bg-white border border-[#e5e2dc] p-8 transition-all duration-500 hover:border-[#d4782c] ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`article-card-${article.slug}`}
    >
      <div className="flex items-center gap-4 mb-4">
        <span className="text-[#d4782c] font-dm-sans text-xs font-semibold uppercase tracking-wider">
          {article.category}
        </span>
        <span className="text-[#4a4744] font-dm-sans text-xs">
          {formatDate(article.publication_date || article.created_at)}
        </span>
      </div>
      
      <h2 className="font-playfair text-xl md:text-2xl font-bold text-[#1a1a1a] mb-4 leading-tight">
        <Link 
          to={`/insights/${article.slug}`}
          className="hover:text-[#d4782c] transition-colors duration-200"
        >
          {article.title}
        </Link>
      </h2>
      
      <p className="text-[#4a4744] leading-relaxed mb-6">
        {article.excerpt}
      </p>
      
      <Link 
        to={`/insights/${article.slug}`}
        className="inline-flex items-center gap-2 text-[#d4782c] font-dm-sans font-semibold text-sm hover:gap-3 transition-all duration-200"
        data-testid={`read-more-${article.slug}`}
      >
        Read more
        <span>→</span>
      </Link>
    </article>
  );
};

const ArticlesGrid = ({ articles, loading }) => {
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white border border-[#e5e2dc] p-8 animate-pulse">
            <div className="h-4 bg-[#e5e2dc] rounded w-1/3 mb-4"></div>
            <div className="h-6 bg-[#e5e2dc] rounded w-full mb-2"></div>
            <div className="h-6 bg-[#e5e2dc] rounded w-2/3 mb-4"></div>
            <div className="h-4 bg-[#e5e2dc] rounded w-full mb-2"></div>
            <div className="h-4 bg-[#e5e2dc] rounded w-full mb-2"></div>
            <div className="h-4 bg-[#e5e2dc] rounded w-1/2"></div>
          </div>
        ))}
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="text-center py-16">
        <p className="text-[#4a4744] text-lg">No articles found in this category.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="articles-grid">
      {articles.map((article, index) => (
        <ArticleCard key={article.id} article={article} index={index} />
      ))}
    </div>
  );
};

export default function InsightsPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  // Fetch articles when category changes
  useEffect(() => {
    const fetchArticles = async () => {
      setLoading(true);
      try {
        const params = activeCategory !== 'All' ? `?category=${activeCategory}` : '';
        const response = await axios.get(`${API}/articles${params}`);
        setArticles(response.data);
      } catch (error) {
        logger.error('Error fetching articles:', error);
        setArticles([]);
      } finally {
        setLoading(false);
      }
    };
    fetchArticles();
  }, [activeCategory]);

  return (
    <Layout>
      <SEO 
        title="Insights"
        path="/insights"
        description="Expert insights on organisational transformation, operating model design, and change leadership from Agilitie's transformation advisors."
      />
      <HeroSection />
      <section className="py-16 md:py-24 bg-white" data-testid="insights-content">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <CategoryFilter 
            activeCategory={activeCategory} 
            onCategoryChange={setActiveCategory} 
          />
          <ArticlesGrid articles={articles} loading={loading} />
        </div>
      </section>
    </Layout>
  );
}
